import { useEffect, useState } from "react";
import InstallAppAlert from "~/src/components/InstallApp/InstallAppAlert";
import { prefixKey } from "~/src/hooks/useLocalState";

export type installMessage = "can-install" | "safari-modern" | "safari-legacy";

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }

  interface Navigator {
    standalone?: boolean; // Non-standard property for iOS Safari
    userAgentData?: {
      platform: string;
    };
  }
}

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export const SHOW_INSTALL_BANNER_KEY = prefixKey({ id: "ShowInstallBanner" });

const InstallApp = () => {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installMessage, setInstallMessage] = useState<installMessage>("safari-legacy");

  const isHidden = localStorage.getItem(SHOW_INSTALL_BANNER_KEY) === "false";

  function handleHideBanner() {
    setShowInstallPrompt(false);
    localStorage.setItem(SHOW_INSTALL_BANNER_KEY, "false");
  }

  function handleInstallClick() {
    if (deferredPrompt) {
      deferredPrompt.prompt().then(() =>
        deferredPrompt.userChoice.then(() => {
          setDeferredPrompt(null);
          handleHideBanner();
        }),
      );
    }
  }

  useEffect(() => {
    const platform = navigator.userAgentData?.platform || navigator.userAgent;
    const isSafari =
      /Safari/.test(navigator.userAgent) &&
      !/Chrome|CriOS|EdgiOS|FxiOS|OPT|DuckDuckGo/.test(navigator.userAgent) &&
      (/iPhone|iPad|iPod|Mac/.test(platform) || /Mac/.test(platform)) &&
      !(navigator as Navigator).standalone;

    if (isSafari) {
      //Detect modern safari versions (26+) by seeing if the browser supports these css features, that are new to Safari 26
      const isModernSafari =
        CSS.supports("width: progress(50%)") || CSS.supports("text-wrap-style: pretty");

      if (isModernSafari) {
        //Set message based on browser, Safari 26 has an extra step to download app.
        setInstallMessage("safari-modern");
      }

      setShowInstallPrompt(!isHidden);
      return;
    }

    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      setDeferredPrompt(e);
      setInstallMessage("can-install");
      setShowInstallPrompt(!isHidden);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  return (
    <div className="relative z-50">
      {showInstallPrompt && (
        <InstallAppAlert
          installMessage={installMessage}
          severity="info"
          canClose
          deferredPrompt={deferredPrompt}
          handleInstallClick={handleInstallClick}
          onClose={() => {
            handleHideBanner();
          }}
        />
      )}
    </div>
  );
};

export default InstallApp;
